import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, EMPTY, Subscription } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap } from 'rxjs/operators';

import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { WhatsAppTemplateCategoryEnum, WhatsAppTemplateStatusEnum, WhatsappTemplate } from '../shared';
import { WhatsAppTemplateUsageEnum } from '../shared/enums/whastapp-template-usage.enum';
import { WhatsappTemplateForm } from '../shared/models/whatsapp-template-form.model';
import { WhatsappTemplatesService } from '../whatsapp-templates.service';

@Component({
  selector: 'app-whatsapp-template',
  templateUrl: './whatsapp-template.component.html',
  styleUrls: ['./whatsapp-template.component.scss'],
})
export class WhatsappTemplateComponent implements OnInit, OnDestroy {
  @Input() create: boolean;

  maxLength = this.whatsappTemplatesService.getLimitations().maxLength;
  form = this.fb.group({
    main: this.fb.group({
      name: [
        null,
        [Validators.required, Validators.pattern('^[a-z0-9_]+$'), Validators.maxLength(this.maxLength.templateName)],
      ],
      languages: [[], [Validators.required, Validators.minLength(1)]],
      siteId: [null, Validators.required],
      usages: [[], [Validators.required, Validators.minLength(1)]],
      status: [{ value: WhatsAppTemplateStatusEnum.approved, disabled: true }, Validators.required],
    }),
    category: [WhatsAppTemplateCategoryEnum.MARKETING, Validators.required],
    templates: this.fb.array([]),
  });

  templateForm: FormGroup;
  template: WhatsappTemplate;
  selectedIndex = 0;
  templateDisplayedBS: BehaviorSubject<FormGroup> = new BehaviorSubject(null);

  get mainForm() {
    return this.form?.get('main') as FormGroup;
  }

  get templatesForm() {
    return this.form?.get('templates') as FormArray;
  }

  get templatesLanguageActive() {
    return this.templatesForm.controls.filter((i: FormGroup) => i.getRawValue().active) as FormGroup[];
  }
  /**
   * The top toolbar save button configuration.
   */
  saveButtonConfig: any;

  languageDisplayed: WhatsappTemplate;

  agentShortcuts: Choice[];
  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    public whatsappTemplatesService: WhatsappTemplatesService,
    private dialog: MatDialog,
    private basicService: BasicService,
    private customRouter: CustomRouter,
    @Inject('$stateParams') private $stateParams: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    // Mechanism to update the breadcrumb in the top toolbar.
    this.topToolbarService.setTitleTranslation('parameters/whatsapp-templates/title', null);
    this.topToolbarService.setButton('save', () => {
      this.askForSave(this.form.getRawValue());
    });
    this.topToolbarService.setSecondaryButton(
      'parameters/whatsapp-templates/synchronize-with-meta-button',
      () => this.synchronizeStatuses(),
      null,
      {
        iconPath: 'ajs-assets/img/refresh.svg',
        testId: 'synchronize-meta',
      }
    );
  }

  ngOnInit() {
    this.subscriptions.add(
      this.mainForm.get('siteId').valueChanges.subscribe(async siteId => {
        if (siteId) {
          this.agentShortcuts = await this.whatsappTemplatesService.getAgentShortcutsForAccount(siteId);
          const agentShortcutsSet: Set<string> = this.agentShortcuts.reduce((result, item) => {
            result.add(item.id as string);
            return result;
          }, new Set<string>());
          this.whatsappTemplatesService.cleanInvalidAgentShortcutsFromQuickRepliesBS.next(agentShortcutsSet);
        }
      })
    );
    if (!this.create) {
      this.basicService.showSpinner();

      this.whatsappTemplatesService
        .findOne(this.$stateParams.siteId, this.$stateParams.templateElementName)
        .pipe(finalize(() => this.basicService.hideSpinner()))
        .subscribe({
          next: (template: WhatsappTemplate) => {
            this.whatsappTemplatesService.headerMediaFileBS.next(null);
            this.template = template;
            this.addFormListeners();
            this.form.get('main.languages').setValue(template.main.languages);
            this.form.patchValue(template);
            if (template.isElementEditable) {
              this.form.get('main').disable({ emitEvent: false });
              // In edition only the usages can be modified.
              this.form.get('main.usages').enable();
            } else {
              this.form.disable();
            }
          },
        });
    } else {
      this.addFormListeners();
    }
  }

  private synchronizeStatuses() {
    this.basicService.showSpinner();
    this.whatsappTemplatesService
      .synchronizeStatuses()
      .pipe(
        catchError(() => {
          this.basicService.toastAlert('bug/unexpected-error');
          return EMPTY;
        }),
        switchMap(() =>
          this.$stateParams.templateElementName
            ? this.whatsappTemplatesService.findOne(this.$stateParams.siteId, this.$stateParams.templateElementName)
            : EMPTY
        ),
        finalize(() => this.basicService.hideSpinner())
      )
      .subscribe({
        next: (template: WhatsappTemplate) => {
          this.form.get('main.status').setValue(template.main.status);
        },
        error: () => this.basicService.toastAlert('bug/unexpected-error'),
      });
  }

  private addFormListeners() {
    this.topToolbarService.updateButtonDisable(true);

    this.subscriptions.add(
      this.form.get('category').valueChanges.subscribe(value => {
        if (value) {
          this.canManageLimitedTimeOffer();
        }
      })
    );

    this.subscriptions.add(
      this.form.get('main.usages').valueChanges.subscribe(value => {
        if (value) {
          this.canManageLimitedTimeOffer();
        }
      })
    );

    this.subscriptions.add(
      this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        this.templateDisplayedBS.next(this.templatesLanguageActive[this.selectedIndex]?.controls.template as FormGroup);
        const hasAtLeastOneLimitedTimeOffer = (this.form.get('templates') as FormArray).controls.some(
          (templateControl: FormGroup) =>
            templateControl.get('active').value && templateControl.getRawValue().template?.limitedTimeOfferEnabled
        );
        if (hasAtLeastOneLimitedTimeOffer) {
          this.form.get('main.usages').disable({ emitEvent: false });
        } else {
          this.form.get('main.usages').enable({ emitEvent: false });
        }
        const invalid =
          this.form.pristine ||
          this.form.get('main').invalid ||
          this.form.get('category').invalid ||
          (this.form.get('templates') as FormArray).controls.some(
            (templateControl: FormGroup) => templateControl.get('active').value && templateControl.invalid
          );
        this.topToolbarService.updateButtonDisable(invalid);
      })
    );

    this.subscriptions.add(
      this.form.get('main.languages').valueChanges.subscribe(languagesCodes => {
        if (languagesCodes?.length) {
          languagesCodes.forEach(languageCode => {
            const hasTemplate = this.templatesForm.getRawValue().some(template => template.code === languageCode);
            if (!hasTemplate) {
              this.templatesForm.push(
                this.fb.group({
                  code: [languageCode],
                  active: [true],
                  template: this.fb.group({
                    header: this.fb.group({
                      type: ['text'], // text | media
                      media_type: [null],
                      // In case of text.
                      text: [null, [Validators.maxLength(this.maxLength.header)]],
                      // In case of image | document | video (picture, video or file).
                      file_handle: [null],
                      media_url: [null],
                      media_id: [null],
                      filename: [null],
                      static: [null],
                    }),
                    limitedTimeOfferEnabled: [{ value: false, disabled: true }],
                    limited_time_offer: this.fb.group({
                      text: [
                        { value: null, disabled: true },
                        [Validators.required, Validators.maxLength(this.maxLength.textLTO)],
                      ],
                    }),
                    body: [null, [Validators.required, Validators.maxLength(this.maxLength.body)]],
                    footer: [null, Validators.maxLength(this.maxLength.footer)],
                    buttons: this.fb.array([]),
                    placeholders: this.fb.group({}),
                  }),
                })
              );
            } else {
              this.templatesForm.controls.forEach((control: FormGroup) => {
                const { code } = control.getRawValue();
                control.get('active').setValue(languagesCodes.includes(code));
              });
            }
          });
          if (!this.templateForm) {
            this.templateForm = this.templatesForm?.controls[this.selectedIndex] as FormGroup;
          }
        }
      })
    );

    this.templateForm = this.templatesForm?.controls[this.selectedIndex] as FormGroup;
  }

  private canManageLimitedTimeOffer(options?: { stopPropagation: boolean }) {
    const { category, main } = this.form.getRawValue();
    const { usages } = main;

    const result =
      category === WhatsAppTemplateCategoryEnum.MARKETING &&
      usages?.length === 1 &&
      usages[0] === WhatsAppTemplateUsageEnum.bot;

    if (options?.stopPropagation) {
      return result;
    }

    this.whatsappTemplatesService.canActivateLimitedTimeOfferBS.next(result);
    return result;
  }

  selectedIndexChange({ index }) {
    this.selectedIndex = index;
    this.templateForm = this.templatesLanguageActive[index];
    this.templateDisplayedBS.next(this.templatesLanguageActive[index]?.controls.template as FormGroup);
  }

  /**
   * Allows to ask the user to confirm to save the template due to the constraints limitations from Whatsapp.
   * @param template the template to save.
   */
  askForSave(template: WhatsappTemplate) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.basicService.translate('parameters/whatsapp-templates/save/title'),
        description: this.basicService.translate('parameters/whatsapp-templates/save/confirm', {
          templateName: template.main.name,
        }),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('continue'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.basicService.showSpinner();
        this.topToolbarService.updateButtonDisable(true);
        const model: WhatsappTemplateForm = this.form.getRawValue();
        (model.templates || []).forEach(language => this.encodeLanguagePart(language));

        this.whatsappTemplatesService
          .save(model, this.create)
          .pipe(finalize(() => this.basicService.hideSpinner()))
          .subscribe({
            next: () => {
              this.basicService.toastSuccess('OK');
              const { siteId, name } = model.main;
              if (this.create) {
                this.whatsappTemplatesService.navigateToDetails(name, siteId);
              } else {
                this.customRouter.reload();
              }
            },
          });
      }
    });
  }

  private encodeLanguagePart(language: any) {
    try {
      if (language.template.header.type === 'text' && !language.template.header.text) {
        delete language.template.header;
      }
      const canManageLimitedTimeOffer = this.canManageLimitedTimeOffer({ stopPropagation: true });
      if (!canManageLimitedTimeOffer || !language.template.limitedTimeOfferEnabled) {
        delete language.template.limited_time_offer;
      } else if (language.template.limitedTimeOfferEnabled) {
        delete language.template.footer;
      }
    } catch {
      // Do nothing;
    }
    // Never send the following fields to the BE.
    delete language.template.limitedTimeOfferEnabled;
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    this.topToolbarService.clearButtons();
  }
}
